import {
  AttributionController_staff_v1,
  AttributionController_terminals_v1,
  type AttributionRateResource,
  type OutletResource,
  OutletsController_list_v1,
  type StaffAttributionDayResource,
  type StaffAttributionReportResource,
  type StaffAttributionResource,
  type TerminalAttributionDayResource,
  type TerminalAttributionReportResource,
  type TerminalAttributionResource,
  type UnattributedActivityResource,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Checkbox } from '@repo/ui/components/checkbox';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, isNotFound } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import {
  type AttributionMeasure,
  DEFAULT_STAFF_MEASURE,
  DEFAULT_TERMINAL_MEASURE,
  describeRate,
  formatRateFraction,
  formatRatePercent,
  measureLabel,
  STAFF_MEASURES,
  TERMINAL_MEASURES,
} from './attribution-measures';
import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';

/**
 * Who recorded what, and which till it was recorded at:
 * `GET /v1/reports/attribution/staff` and
 * `GET /v1/reports/attribution/terminals`.
 *
 * ## The reads rank; they do not judge
 *
 * This is the constraint that shaped this screen more than any layout decision,
 * and `attribution-measures.ts` beside it carries the long form. In short: the
 * API publishes **no verdict of any kind** — nothing in either resource asserts
 * that a figure is unusual, and `blossom-rest-api` fails its own build if such a
 * field is ever added (CL-902). That decision is inherited here.
 *
 * So there is **no styling anywhere on this screen conditional on a rate**. No
 * red row, no warning icon, no badge earned by a number, no client-side
 * comparison of a rate against a value, configurable or otherwise. Every row is
 * drawn identically; what differs between two rows is their figures, which is
 * the only thing that differs between two rows in the answer.
 *
 * `Attribution.test.tsx` asserts it twice, because a rule kept only by
 * intention is a rule the next edit breaks: once by rendering a
 * hundred-per-cent row beside a two-per-cent one and requiring the markup to be
 * identical, and once by sweeping this source. The second is the client half of
 * the API's own sweep.
 *
 * A staff member accused by a screen the API was designed not to let accuse them
 * is exactly the outcome all of this was decided against. The ranking is a
 * ranking; the reading of it is a person's.
 *
 * ## Every rate carries its denominator, everywhere it appears
 *
 * `{numerator: 1, denominator: 1, rate: 1}` is one refund out of one sale. It
 * sorts to the top of a refund ranking, it is `100%`, and it is a quiet Tuesday
 * rather than the worst case in the park. `RateCell` therefore cannot render a
 * percentage without its fraction — there is no prop for it and no second
 * component that does — and the day breakdown uses the same cell for the same
 * reason: a rate over one Tuesday and a rate over a month are different facts,
 * and only the denominator says which one you are looking at.
 *
 * ## Refunds cannot be attributed to a till, and the screen says so
 *
 * `sale_refunds` carries no `terminal_id` (`blossom-rest-api#421`, CL-904), so
 * no refund in this park can be grouped by a device. The read **publishes** that
 * rather than concealing it: every terminal row is simply without a refund
 * figure, `refundRate` is refused as a ranking, and the period's refunds arrive
 * on an `unattributed` object with `refundsCarryTerminal: false` and a prose
 * `note` the document declares stable and safe to display verbatim.
 *
 * The failure this guards against is a reader taking an absent refund column for
 * a refund count of zero. So the terminal view has **no refund column at all** —
 * a column of dashes invites exactly that misreading — and states the limitation
 * under the table in the screen's own words, then in the API's, with the
 * period's actual refunds beside it.
 *
 * ## The period, and how long it can take
 *
 * `from` and `to` are required, so the screen opens on a period rather than on
 * nothing; `reporting-period.ts` holds the default, the three refusals and the
 * clock question. These two reads exceed the 200 ms budget at long periods
 * (`blossom-rest-api#456`), which is why the table keeps its head and its shape
 * throughout and fills with a skeleton rather than a spinner: at a year the wait
 * is long enough to be noticed, and a page that has already drawn its columns is
 * one a reader can orient in while it fills.
 *
 * ## What is deliberately not here
 *
 * **`hourly`.** Both resources publish an hour distribution per subject, and the
 * unattributed group publishes its own — the document argues, rightly, that the
 * hour a refund was taken at is the fact an investigation wants. Rendering a
 * distribution is a screen's worth of work rather than a column's, and no line
 * of this ticket's acceptance asks for it, so it is left for a ticket of its own
 * rather than squeezed into a cell here. Named in the PR body.
 */

/** Which of the two reads the screen is showing. */
type Subject = 'staff' | 'terminals';

const SUBJECTS: readonly { readonly value: Subject; readonly label: string }[] = [
  { value: 'staff', label: 'Staff' },
  { value: 'terminals', label: 'Tills' },
];

/** Nothing chosen: the read then covers every outlet the caller reaches. */
const EVERY_OUTLET = '';

/**
 * Every state the list can be in. One value, so two cannot coexist — and the
 * loaded shape carries its own subject, so a report that lands after the reader
 * has switched lists is discarded rather than rendered under the wrong headers.
 */
type ListState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly subject: 'staff';
      readonly report: StaffAttributionReportResource;
    }
  | {
      readonly kind: 'loaded';
      readonly subject: 'terminals';
      readonly report: TerminalAttributionReportResource;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

export function Attribution() {
  const client = useApiClient();

  const [subject, setSubject] = useState<Subject>('staff');
  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [byDay, setByDay] = useState(false);

  // One held measure per list rather than one shared. The two closed sets
  // overlap but are not the same set — there is no refund measure for a till —
  // so a shared value would have to be reset on every switch, and switching
  // back would have lost what the reader had chosen.
  const [staffMeasure, setStaffMeasure] = useState(DEFAULT_STAFF_MEASURE);
  const [terminalMeasure, setTerminalMeasure] = useState(DEFAULT_TERMINAL_MEASURE);

  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  const [state, setState] = useState<ListState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  const problem = periodProblem(period, systemClock);

  // The park's own day, in Asia/Kolkata — not the device's. Read once per render
  // rather than held in state: a screen left open across midnight should bound
  // itself at the new day.
  const today = currentBusinessDate(systemClock);

  const orderBy = subject === 'staff' ? staffMeasure : terminalMeasure;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones included —
    // this is a report over a past period, and an outlet retired since it still
    // traded during it.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // The picker is the only casualty; the report reads every outlet in
        // reach without one.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request: the notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (period.from === '' || period.to === '' || problem !== null) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    // `outletId` is omitted rather than sent empty. It is a conjunct beside the
    // caller's reach, so naming an outlet can only subtract; naming nothing is
    // every outlet the owner reaches, which is what this screen opens on.
    const range = {
      from: period.from,
      to: period.to,
      byDay,
      ...(outletId === EVERY_OUTLET ? {} : { outletId }),
    };

    // The two branches are written out rather than sharing one query object with
    // a cast. The `orderBy` sets are different closed unions — there is no
    // refund measure for a till — and spreading one query into both operations
    // would need an `any` to compile, which is exactly the check worth keeping:
    // a measure sent to the wrong read is a `400`, and the compiler can see it.
    const answer =
      subject === 'staff'
        ? client
            .request(AttributionController_staff_v1, {
              query: { ...range, orderBy: staffMeasure },
              signal: controller.signal,
            })
            .then((report) => ({ subject: 'staff' as const, report }))
        : client
            .request(AttributionController_terminals_v1, {
              query: { ...range, orderBy: terminalMeasure },
              signal: controller.signal,
            })
            .then((report) => ({ subject: 'terminals' as const, report }));

    answer
      .then((landed) => {
        if (!live) return;

        setState(
          landed.subject === 'staff'
            ? { kind: 'loaded', subject: 'staff', report: landed.report }
            : { kind: 'loaded', subject: 'terminals', report: landed.report },
        );
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [
    client,
    subject,
    outletId,
    staffMeasure,
    terminalMeasure,
    byDay,
    period.from,
    period.to,
    problem,
    reloads,
  ]);

  // Base UI resolves what a trigger shows from `items` and not from the rendered
  // options, so a picker without it shows a bare UUID where a name belongs.
  // Derived from the same rows the options render.
  const outletItems = useMemo(
    () => [
      { label: 'Every outlet in reach', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const measures: readonly AttributionMeasure<string>[] =
    subject === 'staff' ? STAFF_MEASURES : TERMINAL_MEASURES;

  const measureItems = useMemo(
    () => measures.map((measure) => ({ label: measure.label, value: measure.value })),
    [measures],
  );

  const columns = subject === 'staff' ? STAFF_COLUMNS : TERMINAL_COLUMNS;

  const byDayId = useId();

  return (
    <Screen
      title="Attribution"
      lede="who recorded what, and where"
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="attribution-subject" className="text-sm text-muted-foreground">
              Attribute to
            </Label>
            <Select
              value={subject}
              items={SUBJECTS}
              onValueChange={(next) => {
                setSubject((next ?? 'staff') as Subject);
              }}
            >
              <SelectTrigger
                id="attribution-subject"
                aria-label="Attribute to"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {SUBJECTS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attribution-order" className="text-sm text-muted-foreground">
              Ranked by
            </Label>
            <Select
              value={orderBy}
              items={measureItems}
              onValueChange={(next) => {
                if (next === null) return;

                // Narrowed by the list it came from: the two closed sets are not
                // the same set, and a value from one is not a value for the
                // other.
                if (subject === 'staff') {
                  const chosen = STAFF_MEASURES.find((measure) => measure.value === next);

                  if (chosen !== undefined) setStaffMeasure(chosen.value);
                } else {
                  const chosen = TERMINAL_MEASURES.find((measure) => measure.value === next);

                  if (chosen !== undefined) setTerminalMeasure(chosen.value);
                }
              }}
            >
              <SelectTrigger
                id="attribution-order"
                aria-label="Ranked by"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {measures.map((measure) => (
                    <SelectItem key={measure.value} value={measure.value}>
                      {measure.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attribution-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                setOutletId(next ?? EVERY_OUTLET);
              }}
            >
              <SelectTrigger
                id="attribution-outlet"
                aria-label="Outlet"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={EVERY_OUTLET}>Every outlet in reach</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="attribution-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="attribution-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, from: next }));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="attribution-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="attribution-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              // The route refuses a period ending after the park's current
              // trading day. The notice below still stands — the day rolls at
              // midnight in Kolkata and a screen left open overnight would
              // otherwise bound itself at yesterday.
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, to: next }));
              }}
            />
          </div>

          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <div className="flex items-start gap-2.5">
            {/* Base UI's checkbox is a `button` with `role="checkbox"` rather
                than an `input`, so a `<label for>` would bind to nothing.
                `aria-labelledby` is what names it. */}
            <Checkbox
              aria-labelledby={byDayId}
              checked={byDay}
              onCheckedChange={(next) => {
                setByDay(next === true);
              }}
              className="mt-0.5"
            />
            <span id={byDayId} className="text-sm text-foreground">
              Show each business day
            </span>
          </div>

          <ScreenFact label="Period">
            {period.from === '' || period.to === ''
              ? 'Choose both days.'
              : `${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}`}
          </ScreenFact>

          <ScreenFact label="Ranked by">{measureLabel(measures, orderBy)}</ScreenFact>
        </>
      }
    >
      <ScreenTable label={subject === 'staff' ? 'Staff attribution' : 'Till attribution'}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.label} className={column.numeric === true ? 'text-right' : ''}>
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns.length} rows={6} />}

        {state.kind === 'idle' && (
          <TableNotice columns={columns.length}>
            <EmptyState title="Choose a period to report on" description="Choose both days." />
          </TableNotice>
        )}

        {state.kind === 'failed' && (
          <TableNotice columns={columns.length}>
            {isForbidden(state.error) ? (
              <EmptyState
                title="This account cannot read attribution"
                description="If you are the owner, sign out and back in."
              />
            ) : isNotFound(state.error) ? (
              // The route answers 404 for an outlet that does not exist and for
              // one outside the caller's reach, identically and on purpose.
              // Guessing which is precisely the disclosure it declined to make.
              <EmptyState
                title="That outlet is not one this account can report on"
                description="Choose another outlet."
              />
            ) : (
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
                title="This report could not be read"
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          state.subject === 'staff' &&
          (state.report.staff.length === 0 ? (
            <TableNotice columns={columns.length}>
              <EmptyState
                title="Nobody recorded anything in this period"
                description="Widen the period, or choose another outlet."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.report.staff.map((person) => (
                <StaffRows key={person.userId} person={person} />
              ))}
            </TableBody>
          ))}

        {state.kind === 'loaded' &&
          state.subject === 'terminals' &&
          (state.report.terminals.length === 0 ? (
            <TableNotice columns={columns.length}>
              <EmptyState
                title="No till recorded anything in this period"
                description="Widen the period, or choose another outlet."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.report.terminals.map((till) => (
                <TerminalRows key={till.terminalId} till={till} />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && state.subject === 'terminals' && (
        <Unattributed group={state.report.unattributed} />
      )}
    </Screen>
  );
}

/** A column, and whether its figures right-align. */
interface Column {
  readonly label: string;
  readonly numeric?: boolean;
}

const STAFF_COLUMNS: readonly Column[] = [
  { label: 'Staff' },
  { label: 'Transactions', numeric: true },
  { label: 'Takings', numeric: true },
  { label: 'Average', numeric: true },
  { label: 'Refund rate', numeric: true },
  { label: 'Void rate', numeric: true },
  { label: 'Reprint rate', numeric: true },
  { label: 'Handed back', numeric: true },
  { label: 'Voided', numeric: true },
];

/**
 * The till columns, and **there is no refund column among them**.
 *
 * Not an omission and not a column of dashes: no refund in this park carries a
 * terminal, so there is no honest figure to put in one, and a column of blanks
 * is what a reader takes for a count of zero. The note under the table says so,
 * and the period's refunds are published there.
 */
const TERMINAL_COLUMNS: readonly Column[] = [
  { label: 'Till' },
  { label: 'Outlet' },
  { label: 'Transactions', numeric: true },
  { label: 'Takings', numeric: true },
  { label: 'Average', numeric: true },
  { label: 'Void rate', numeric: true },
  { label: 'Reprint rate', numeric: true },
  { label: 'Voided', numeric: true },
];

/**
 * One person's row, and their day breakdown under it when one was asked for.
 *
 * A fragment of rows rather than a nested table: the days are the same measures
 * over a shorter period, so they belong in the same columns, and a reader
 * comparing a Tuesday against the month should not have to compare across two
 * grids to do it.
 */
function StaffRows({ person }: { person: StaffAttributionResource }) {
  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-mono text-muted-foreground numeric">{person.staffCode}</span>
            <span className="text-foreground">{person.displayName}</span>
            {person.deactivated && (
              // `outline` and not `destructive`: a departure is an ordinary end
              // to an ordinary job, and they still recorded what they recorded.
              <Badge variant="outline">Left</Badge>
            )}
          </div>
        </TableCell>
        <Count>{person.transactionCount}</Count>
        <Money>{person.takingsPaise}</Money>
        <Money>{person.averageTransactionValuePaise}</Money>
        <RateCell rate={person.refundRate} />
        <RateCell rate={person.voidRate} />
        <RateCell rate={person.reprintRate} />
        <Money>{person.refundedValuePaise}</Money>
        <Money>{person.voidedValuePaise}</Money>
      </TableRow>

      {person.days?.map((day) => (
        <StaffDayRow key={day.businessDate} day={day} />
      ))}
    </>
  );
}

/**
 * One business day of one person's figures.
 *
 * The rates are rendered by the same cell as the range above, with the same
 * denominators, for the reason the resource gives: the counts and the money add
 * up to the row above and **the rates do not**, because a ratio never sums. A
 * sixty-per-cent refund rate over five sales on one Tuesday and an unremarkable
 * rate over the month are both true, and this is where the first becomes
 * visible — with the `5` beside it that says how much weight it carries.
 */
function StaffDayRow({ day }: { day: StaffAttributionDayResource }) {
  return (
    <TableRow>
      <TableCell>
        <DayLabel businessDate={day.businessDate} state={day.state} />
      </TableCell>
      <Count>{day.transactionCount}</Count>
      <Money>{day.takingsPaise}</Money>
      <Money>{day.averageTransactionValuePaise}</Money>
      <RateCell rate={day.refundRate} />
      <RateCell rate={day.voidRate} />
      <RateCell rate={day.reprintRate} />
      <Money>{day.refundedValuePaise}</Money>
      <Money>{day.voidedValuePaise}</Money>
    </TableRow>
  );
}

/** One till's row, and its day breakdown when one was asked for. */
function TerminalRows({ till }: { till: TerminalAttributionResource }) {
  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-mono text-muted-foreground numeric">{till.prefix}</span>
            <span className="text-foreground">{till.name}</span>
            {(till.status === 'REVOKED' || till.deleted) && (
              // The till kept everything it took, so this says what it is now
              // rather than marking the row as a problem.
              <Badge variant="outline">Revoked</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">{till.outletName}</TableCell>
        <Count>{till.transactionCount}</Count>
        <Money>{till.takingsPaise}</Money>
        <Money>{till.averageTransactionValuePaise}</Money>
        <RateCell rate={till.voidRate} />
        <RateCell rate={till.reprintRate} />
        <Money>{till.voidedValuePaise}</Money>
      </TableRow>

      {till.days?.map((day) => (
        <TerminalDayRow key={day.businessDate} day={day} />
      ))}
    </>
  );
}

/** One business day of one till's figures. */
function TerminalDayRow({ day }: { day: TerminalAttributionDayResource }) {
  return (
    <TableRow>
      <TableCell>
        <DayLabel businessDate={day.businessDate} state={day.state} />
      </TableCell>
      <TableCell />
      <Count>{day.transactionCount}</Count>
      <Money>{day.takingsPaise}</Money>
      <Money>{day.averageTransactionValuePaise}</Money>
      <RateCell rate={day.voidRate} />
      <RateCell rate={day.reprintRate} />
      <Money>{day.voidedValuePaise}</Money>
    </TableRow>
  );
}

/**
 * A day inside a subject's breakdown: the date, indented, and whether it is
 * settled yet.
 *
 * `state` is `OPEN`, `CLOSED`, or null where the date has activity but no
 * reachable outlet opened a business day on it. An open day's figures are
 * provisional and the row says so, which is the whole reason the field is
 * published — this is a statement about reconciliation, not about the figures.
 */
function DayLabel({ businessDate, state }: { businessDate: string; state: string | null }) {
  return (
    <span className="flex items-center gap-2 ps-6 text-sm text-muted-foreground">
      {formatBusinessDate(businessDate)}
      {state === 'OPEN' && <Badge variant="outline">Not closed yet</Badge>}
    </span>
  );
}

/**
 * A rate, as its percentage over the fraction it came from.
 *
 * **There is no way to render one without the other**, and that is the point of
 * this being a component rather than a call to a formatter at each of the ten
 * sites that need it. `100%` alone turns one refund out of one sale into an
 * accusation; `1 / 1` beneath it turns it back into a quiet Tuesday.
 *
 * Nothing about how this is drawn depends on the value. There is no branch here
 * on the size of the rate and there must not be one — see this file's head, and
 * the two assertions in `Attribution.test.tsx` that hold it.
 *
 * The visible pair is `aria-hidden` and a sentence takes its place for anybody
 * listening: "5.0% 10 / 198" run together is ambiguous enough to be worth saying
 * properly once.
 */
function RateCell({ rate }: { rate: AttributionRateResource }) {
  return (
    <TableCell className="text-right">
      <span className="sr-only">{describeRate(rate)}</span>
      <span aria-hidden className="block font-mono text-foreground numeric">
        {formatRatePercent(rate)}
      </span>
      <span aria-hidden className="block font-mono text-xs text-muted-foreground numeric">
        {formatRateFraction(rate)}
      </span>
    </TableCell>
  );
}

/** A count, right-aligned and set in figures that do not jitter down a column. */
function Count({ children }: { children: number }) {
  return <TableCell className="text-right font-mono numeric">{children}</TableCell>;
}

/**
 * An amount in whole paise, formatted only here at the render edge.
 *
 * `null` is a real answer on `averageTransactionValuePaise` — somebody who
 * refunded, voided or reprinted but recorded no transaction has no average — and
 * it is shown as a dash rather than as zero, for the reason a null rate is not
 * nought per cent.
 */
function Money({ children }: { children: number | null }) {
  return (
    <TableCell className="text-right font-mono numeric">
      {children === null ? <span className="text-muted-foreground">—</span> : formatPaise(children)}
    </TableCell>
  );
}

/**
 * What happened in the period that belongs to no till, and why.
 *
 * **This is the published gap, surfaced rather than hidden.** Every refund in
 * the period is here, because `sale_refunds` has no terminal column at all
 * (`blossom-rest-api#421`, CL-904) — not because these particular refunds could
 * not be placed. Back-office voids and reprints join them for a different
 * reason: their column exists and is null, because no device originated them.
 *
 * It is a section under the table rather than a row in it because it is not a
 * till: it has no takings, no transactions and no rates, and a row of dashes
 * across six columns would be a worse answer than a plain statement.
 *
 * The API's own `note` is rendered verbatim beneath the screen's sentence. The
 * document declares it stable text and safe to display, and it names the issue
 * the fix is tracked under — which is exactly what somebody who wants the refund
 * column to exist should be reading.
 */
function Unattributed({ group }: { group: UnattributedActivityResource }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="mt-6 border-t border-border pt-5">
      <h2 id={headingId} className="font-medium text-foreground">
        Belonging to no till
      </h2>

      <p className="mt-1.5 max-w-prose text-sm text-muted-foreground">
        Refunds cannot be attributed to a till, so they are all here.
      </p>

      <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <Figure term="Refunds recorded">{String(group.refundCount)}</Figure>
        <Figure term="Handed back">{formatPaise(group.refundedValuePaise)}</Figure>
        <Figure term="Voided from the back office">{String(group.voidCount)}</Figure>
        <Figure term="Value of those voids">{formatPaise(group.voidedValuePaise)}</Figure>
        <Figure term="Reprinted from the back office">{String(group.reprintCount)}</Figure>
      </dl>

      {/* The API's own words, which its document publishes as stable text and
          declares safe to show verbatim. Kept beside the screen's sentence
          rather than instead of it: this one names the column and the issue,
          which is what somebody chasing the gap needs, and the sentence above is
          what somebody reading the report needs. */}
      <p className="mt-4 max-w-prose text-xs leading-relaxed text-muted-foreground">{group.note}</p>
    </section>
  );
}

/** One labelled figure in the unattributed group. */
function Figure({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border pb-2">
      <dt className="text-sm text-muted-foreground">{term}</dt>
      <dd className="font-mono text-foreground numeric">{children}</dd>
    </div>
  );
}
